import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const TABS = [
  { key: "robots", label: "robots.txt AI", icon: "🤖" },
  { key: "indexnow", label: "IndexNow", icon: "⚡" },
  { key: "google_index", label: "Google Index", icon: "🔍" },
  { key: "faq", label: "FAQ/Капсули", icon: "❓" },
];

// File preview component
function FilePreview({ content, filename, dark, t, card, onCopy, onDownload, onEdit }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {onCopy && <button onClick={onCopy} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "none", color: t.text, cursor: "pointer", fontFamily: "inherit" }}>Копіювати</button>}
        {onDownload && <button onClick={onDownload} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "none", color: t.text, cursor: "pointer", fontFamily: "inherit" }}>Завантажити</button>}
        {onEdit && <button onClick={onEdit} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "none", color: t.text, cursor: "pointer", fontFamily: "inherit" }}>Редагувати</button>}
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "6px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b" }} />
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} />
          <span style={{ flex: 1, textAlign: "center", fontSize: 11, color: t.textMuted }}>{filename}</span>
        </div>
        <pre style={{ margin: 0, padding: 16, fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", color: t.text, background: dark ? "#0d0d1a" : "#fafbfc", maxHeight: 500, overflowY: "auto" }}>
          {content}
        </pre>
      </div>
    </div>
  );
}

export default function AeoView() {
  const { proj, activeId, apiFetch, t, dark, card, btnP, btnG, btnSm, stitle, muted, flash, inp, bgTasks } = useApp();
  const [tab, setTab] = useState("robots");
  const [generating, setGenerating] = useState(false);

  // Data state
  const [robots, setRobots] = useState(null);
  const [indexNow, setIndexNow] = useState(null);
  const [gIndex, setGIndex] = useState({ status: null, log: [] });
  const [gIndexUrls, setGIndexUrls] = useState("");
  const [faq, setFaq] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState("");

  const domain = (proj?.domain || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");

  // Map backend FAQ shape { urls: [{url, faqs, jsonLd}] } → { pages: [{url, items, schema}] }
  const mapFaq = (d) => {
    if (!d || !Array.isArray(d.urls)) return null;
    return {
      pages: d.urls.map((u) => ({
        url: u.url,
        items: u.faqs || [],
        schema: u.jsonLd || {},
      })),
    };
  };

  // Load all data on mount
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/robots-ai`).then(r => r.json()).then(d => { if (d?.content) setRobots(d); }).catch(() => {});
    apiFetch(`/api/projects/${activeId}/indexnow/log`).then(r => r.json()).then(d => { if (d) setIndexNow(d); }).catch(() => {});
    Promise.all([
      apiFetch(`/api/projects/${activeId}/google-index/status`).then(r => r.json()).catch(() => null),
      apiFetch(`/api/projects/${activeId}/google-index/log`).then(r => r.json()).catch(() => null),
    ]).then(([status, logResp]) => { setGIndex({ status, log: logResp?.log || [] }); });
    apiFetch(`/api/projects/${activeId}/faq-generator`).then(r => r.json()).then(d => { setFaq(mapFaq(d)); }).catch(() => {});
  }, [activeId]);

  // Watch background tasks for auto-reload
  useEffect(() => {
    const reload = (type, setter, endpoint, mapper) => {
      const task = (bgTasks || []).find(bt => bt.projectId === activeId && bt.type === type);
      if (task?.status === "done") {
        apiFetch(`/api/projects/${activeId}/${endpoint}`).then(r => r.json()).then(d => { setter(mapper ? mapper(d) : d); }).catch(() => {});
        setGenerating(false);
      } else if (task?.status === "running") {
        setGenerating(true);
      }
    };
    reload("faq_generate", setFaq, "faq-generator", mapFaq);
  }, [bgTasks]);

  const copy = (text) => { navigator.clipboard.writeText(text); flash("Скопійовано"); };
  const download = (text, filename) => { const b = new Blob([text], { type: "text/plain;charset=utf-8" }); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click(); };


  // Simple POST → background task handlers
  const startTask = async (endpoint, label, force = false) => {
    setGenerating(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/${endpoint}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ all: true, force }) });
      const d = await resp.json();
      if (d.error === "too_many_urls") {
        setGenerating(false);
        const msg = d.message + "\n\nНатисніть OK щоб запустити на ВСІХ " + d.total + " URL. Скасувати — щоб не запускати.";
        if (window.confirm(msg)) {
          await startTask(endpoint, label, true);
        }
        return;
      }
      if (d.error) { flash("Помилка: " + d.error); setGenerating(false); }
      else flash(`${label} запущено у фоні`);
    } catch (e) { flash("Помилка: " + e.message); setGenerating(false); }
  };

  const genFaq = () => startTask("faq-generator/generate", "FAQ генерація");

  const genRobots = async () => {
    setGenerating(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/robots-ai/generate`, { method: "POST" });
      const d = await resp.json();
      if (d.content) { setRobots(d); flash("robots.txt згенеровано"); }
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setGenerating(false);
  };

  const submitIndexNow = async () => {
    setGenerating(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/indexnow/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      const d = await resp.json();
      flash(d.ok ? `IndexNow: ${d.urlCount} URL відправлено (${d.message})` : `Помилка: ${d.message}`);
      // Reload log
      const logResp = await apiFetch(`/api/projects/${activeId}/indexnow/log`);
      setIndexNow(await logResp.json());
    } catch (e) { flash("Помилка: " + e.message); }
    setGenerating(false);
  };

  // ── Helpers: pre-fill the Google Index textarea from existing project data ──
  const loadGIndexUrls = async (source) => {
    // source: "clusters" | "structure" | "missingFromLog"
    setGenerating(true);
    try {
      let urls = [];
      // Endpoint /clusters returns an array directly; older code wrapped it as {clusters:[...]}.
      // Accept both shapes.
      const asClusters = (d) => Array.isArray(d) ? d : (Array.isArray(d?.clusters) ? d.clusters : []);
      if (source === "clusters") {
        const r = await apiFetch(`/api/projects/${activeId}/clusters?limit=2000`);
        urls = asClusters(await r.json()).map(c => c.url).filter(Boolean);
      } else if (source === "structure") {
        const r = await apiFetch(`/api/projects/${activeId}/footer/structure`);
        const d = await r.json();
        urls = (d?.pages || []).map(p => p.url).filter(Boolean);
      } else if (source === "missingFromLog") {
        const [cR, lR] = await Promise.all([
          apiFetch(`/api/projects/${activeId}/clusters?limit=2000`),
          apiFetch(`/api/projects/${activeId}/google-index/log?limit=500`),
        ]);
        const cs = asClusters(await cR.json());
        const lD = await lR.json();
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const submitted = new Set((lD?.log || [])
          .filter(e => new Date(e.submitted_at).getTime() > cutoff && e.status === "ok")
          .map(e => e.url));
        urls = cs.map(c => c.url).filter(u => u && !submitted.has(u));
      }
      // Dedup, preserve order
      const seen = new Set();
      const uniq = urls.filter(u => { if (seen.has(u)) return false; seen.add(u); return true; });
      if (!uniq.length) {
        flash(source === "missingFromLog" ? "Усі URL вже відправлені за останні 30 днів" : "Немає URL для завантаження");
      } else {
        setGIndexUrls(uniq.join("\n"));
        flash(`Завантажено ${uniq.length} URL`);
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setGenerating(false);
  };

  const submitGoogleIndex = async (mode) => {
    // mode: "manual" → use textarea; "all" → submit all cluster URLs
    setGenerating(true);
    try {
      let urls;
      if (mode === "manual") {
        urls = gIndexUrls.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
        if (!urls.length) { flash("Вкажіть хоча б один URL"); setGenerating(false); return; }
      } else {
        const r = await apiFetch(`/api/projects/${activeId}/clusters?limit=2000`);
        const data = await r.json();
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.clusters) ? data.clusters : []);
        urls = arr.map(c => c.url).filter(Boolean);
        if (!urls.length) { flash("Немає URL для відправки"); setGenerating(false); return; }
      }
      const resp = await apiFetch(`/api/projects/${activeId}/google-index/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls, type: "URL_UPDATED" }),
      });
      const d = await resp.json();
      if (resp.ok) {
        flash(`Google Index: ${d.ok}/${d.submitted} OK · пропущено ${d.skipped} (квота)`);
        const [s, l] = await Promise.all([
          apiFetch(`/api/projects/${activeId}/google-index/status`).then(r => r.json()),
          apiFetch(`/api/projects/${activeId}/google-index/log`).then(r => r.json()),
        ]);
        setGIndex({ status: s, log: l?.log || [] });
        if (mode === "manual") setGIndexUrls("");
      } else {
        flash(`Помилка: ${d.error || "невідома"}`);
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setGenerating(false);
  };

  const saveEdit = async (endpoint, setter) => {
    try {
      await apiFetch(`/api/projects/${activeId}/${endpoint}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: editContent }) });
      setter(prev => ({ ...prev, content: editContent, editedAt: new Date().toISOString() }));
      setEditing(false); flash("Збережено");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ ...stitle, margin: 0 }}>AEO — AI Engine Optimization</h2>
        <p style={{ ...muted, margin: "4px 0 0", fontSize: 13 }}>Оптимізація сайту для AI-систем (ChatGPT, Claude, Perplexity, Gemini)</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map(tb => (
          <button key={tb.key} onClick={() => { setTab(tb.key); setEditing(false); }} style={{
            padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
            background: tab === tb.key ? "#6366f1" : dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            color: tab === tb.key ? "#fff" : t.text, fontWeight: tab === tb.key ? 700 : 500,
            fontSize: 13, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>{tb.icon}</span> {tb.label}
          </button>
        ))}
      </div>

      {/* ===== robots.txt AI ===== */}
      {tab === "robots" && (
        <div>
          <div style={{ ...card, marginBottom: 16, padding: "10px 16px", borderLeft: "4px solid #f59e0b", fontSize: 13, lineHeight: 1.6 }}>
            Оптимізований <strong>robots.txt</strong>: блокує training-ботів (GPTBot, ClaudeBot), але дозволяє search-ботів (OAI-SearchBot, PerplexityBot, ChatGPT-User).
          </div>
          <div style={{ marginBottom: 12 }}>
            <button onClick={genRobots} disabled={generating} style={{ ...btnP, opacity: generating ? 0.6 : 1 }}>
              {generating ? "Генерація..." : robots ? "Перегенерувати" : "Згенерувати robots.txt"}
            </button>
          </div>
          {robots && !editing && <FilePreview content={robots.content} filename="robots.txt" dark={dark} t={t} card={card}
            onCopy={() => copy(robots.content)} onDownload={() => download(robots.content, "robots.txt")}
            onEdit={() => { setEditContent(robots.content); setEditing(true); }} />}
          {editing && tab === "robots" && (
            <div>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)} style={{ width: "100%", minHeight: 400, padding: 14, borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0d0d1a" : "#fafbfc", color: t.text, fontSize: 12, lineHeight: 1.6, fontFamily: "monospace", resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => saveEdit("robots-ai", setRobots)} style={btnP}>Зберегти</button>
                <button onClick={() => setEditing(false)} style={btnG}>Скасувати</button>
              </div>
            </div>
          )}
          {!robots && !generating && (
            <div style={{ ...card, textAlign: "center", padding: "40px 20px", color: t.textMuted }}>
              Натисніть "Згенерувати" — система завантажить поточний robots.txt і додасть правила для AI-ботів
            </div>
          )}
        </div>
      )}

      {/* ===== IndexNow ===== */}
      {tab === "indexnow" && (
        <div>
          <div style={{ ...card, marginBottom: 16, padding: "10px 16px", borderLeft: "4px solid #ec4899", fontSize: 13, lineHeight: 1.6 }}>
            <strong>IndexNow</strong> — миттєве сповіщення пошукових систем (Bing) та AI про зміни на сайті.
            Замість чекати поки бот знайде зміни — повідомляємо одразу.
          </div>

          {indexNow?.key && (
            <div style={{ ...card, marginBottom: 16, padding: "10px 16px" }}>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 4 }}>Ваш IndexNow ключ:</div>
              <code style={{ fontSize: 13, background: dark ? "#1e1e2e" : "#f1f5f9", padding: "4px 8px", borderRadius: 4 }}>{indexNow.key}</code>
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
                Розмістіть файл <code>{indexNow.key}.txt</code> з вмістом "{indexNow.key}" у кореневій директорії: <code>https://{domain}/{indexNow.key}.txt</code>
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <button onClick={() => {
                  const blob = new Blob([indexNow.key], { type: "text/plain;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `${indexNow.key}.txt`;
                  a.click();
                }} style={{ ...btnP, fontSize: 12 }}>⬇ Завантажити {indexNow.key}.txt</button>
                <button onClick={() => { navigator.clipboard.writeText(indexNow.key); flash("Ключ скопійовано"); }} style={{ ...btnSm, fontSize: 12 }}>📋 Скопіювати ключ</button>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <button onClick={submitIndexNow} disabled={generating} style={{ ...btnP, opacity: generating ? 0.6 : 1 }}>
              {generating ? "Відправка..." : "Відправити всі URL в IndexNow"}
            </button>
          </div>

          {/* Log */}
          {indexNow?.log?.length > 0 && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 700 }}>Історія відправок</div>
              {indexNow.log.map((entry, i) => (
                <div key={i} style={{ padding: "8px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 12, display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ color: entry.status === 200 || entry.status === 202 ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                    {entry.status === 200 || entry.status === 202 ? "✓" : "✗"}
                  </span>
                  <span style={{ color: t.textMuted }}>{new Date(entry.date).toLocaleString("uk-UA")}</span>
                  <span>{entry.urlCount} URL</span>
                  <span style={{ color: t.textMuted }}>{entry.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== Google Indexing API ===== */}
      {tab === "google_index" && (
        <div>
          <div style={{ ...card, marginBottom: 16, padding: "10px 16px", borderLeft: "4px solid #4285f4", fontSize: 13, lineHeight: 1.6 }}>
            <strong>Google Indexing API</strong> — пряма відправка URL у Google для прискореної індексації.
            Замість чекати поки Googlebot прийде на сайт, повідомляємо одразу. Ліміт: <strong>200 URL/день</strong> на проект.
            <br/><span style={{ color: t.textMuted, fontSize: 12 }}>Потребує підключеного GSC з оновленим scope (`indexing`). Якщо GSC підключали раніше — натисніть «Підключити Google» в модулі GSC заново для отримання доступу до Indexing API.</span>
          </div>

          {/* Connection + quota */}
          <div style={{ ...card, marginBottom: 16, padding: "12px 16px", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Статус</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: gIndex.status?.connected ? "#10b981" : "#ef4444", marginTop: 4 }}>
                {gIndex.status?.connected ? "✓ Підключено" : "✗ Не підключено (підключіть GSC)"}
              </div>
            </div>
            {gIndex.status?.connected && (
              <>
                <div style={{ width: 1, height: 32, background: t.border }} />
                <div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Денна квота</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: t.text, marginTop: 4 }}>
                    {gIndex.status?.quotaUsedToday || 0} / {gIndex.status?.quotaDaily || 200}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Залишилось</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: gIndex.status?.quotaRemaining > 50 ? "#10b981" : "#f59e0b", marginTop: 4 }}>
                    {gIndex.status?.quotaRemaining || 0}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Manual URLs textarea */}
          <div style={{ ...card, marginBottom: 16, padding: "12px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6, gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Відправити URL</div>
              <span style={{ fontSize: 11, color: t.textMuted }}>
                {gIndexUrls.split(/\n/).map(s => s.trim()).filter(Boolean).length} у списку
              </span>
            </div>
            {/* Pre-fill helpers */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              <button
                onClick={() => loadGIndexUrls("clusters")}
                disabled={generating}
                style={{ ...btnSm, opacity: generating ? 0.5 : 1, fontSize: 11 }}
                title="Заповнити textarea усіма URL з кластерів"
              >
                📋 З кластерів
              </button>
              <button
                onClick={() => loadGIndexUrls("structure")}
                disabled={generating}
                style={{ ...btnSm, opacity: generating ? 0.5 : 1, fontSize: 11 }}
                title="Заповнити textarea усіма URL зі структури сайту"
              >
                🌳 Зі структури
              </button>
              <button
                onClick={() => loadGIndexUrls("missingFromLog")}
                disabled={generating}
                style={{ ...btnSm, opacity: generating ? 0.5 : 1, fontSize: 11 }}
                title="Тільки ті URL, які ще не відправлені у Google Indexing API за останні 30 днів"
              >
                ✨ Тільки нові (за 30 днів)
              </button>
              {gIndexUrls && (
                <button
                  onClick={() => setGIndexUrls("")}
                  disabled={generating}
                  style={{ ...btnSm, opacity: generating ? 0.5 : 1, fontSize: 11, color: "#ef4444" }}
                  title="Очистити textarea"
                >
                  ✗ Очистити
                </button>
              )}
            </div>
            <textarea
              value={gIndexUrls}
              onChange={(e) => setGIndexUrls(e.target.value)}
              placeholder={`Кожен URL з нового рядка:\nhttps://${domain}/page1\nhttps://${domain}/page2\n\nАбо натисніть кнопку нижче — список заповниться автоматично з кластерів проекту.`}
              style={{ ...inp, width: "100%", minHeight: 120, fontFamily: "monospace", fontSize: 12, marginBottom: 8 }}
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => submitGoogleIndex("manual")} disabled={generating || !gIndex.status?.connected || !gIndexUrls.trim()} style={{ ...btnP, opacity: (generating || !gIndex.status?.connected || !gIndexUrls.trim()) ? 0.5 : 1 }}>
                {generating ? "Відправка..." : "↑ Відправити URL зі списку"}
              </button>
              <button onClick={() => submitGoogleIndex("all")} disabled={generating || !gIndex.status?.connected} style={{ ...btnG, opacity: (generating || !gIndex.status?.connected) ? 0.5 : 1 }}>
                {generating ? "Відправка..." : "↑ Відправити всі URL з кластерів"}
              </button>
              <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>
                Денний ліміт: {gIndex.status?.quotaRemaining || 0} URL
              </span>
            </div>
          </div>

          {/* Log */}
          {gIndex.log?.length > 0 && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 700 }}>Історія відправок ({gIndex.log.length})</div>
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {gIndex.log.map((entry) => (
                  <div key={entry.id} style={{ padding: "8px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 12, display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ color: entry.status === "ok" ? "#10b981" : "#ef4444", fontWeight: 700, width: 14, flexShrink: 0 }}>
                      {entry.status === "ok" ? "✓" : "✗"}
                    </span>
                    <span style={{ color: t.textMuted, fontSize: 11, flexShrink: 0, width: 130 }}>
                      {new Date(entry.submitted_at).toLocaleString("uk-UA")}
                    </span>
                    <span style={{ flex: 1, color: t.text, fontFamily: "monospace", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={entry.url}>{entry.url}</span>
                    <span style={{ color: t.textMuted, fontSize: 10, flexShrink: 0 }}>{entry.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== FAQ/Answer Capsules ===== */}
      {tab === "faq" && (
        <div>
          <div style={{ ...card, marginBottom: 16, padding: "10px 16px", borderLeft: "4px solid #8b5cf6", fontSize: 13, lineHeight: 1.6 }}>
            <strong>FAQ + Answer Capsules</strong> — блоки запитань з відповідями 40-60 слів, оптимізовані для цитування AI.
            Включає FAQPage Schema.org розмітку. Сторінки з FAQ цитуються AI на <strong>40-60% частіше</strong>.
          </div>

          <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={genFaq} disabled={generating} style={{ ...btnP, opacity: generating ? 0.6 : 1 }}>
              {generating ? "Генерація..." : faq?.pages?.length ? "Перегенерувати FAQ" : "Згенерувати FAQ для всіх сторінок"}
            </button>
            {faq?.pages?.length > 0 && (
              <button
                onClick={() => {
                  const totalQ = faq.pages.reduce((s, p) => s + (p.items?.length || 0), 0);
                  const win = window.open("", "_blank");
                  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>FAQ ТЗ — ${domain}</title>
<style>
  @page { margin: 20mm 15mm; size: A4; }
  body { font-family: -apple-system, Arial, sans-serif; font-size: 13px; line-height: 1.6; color: #1a1a2e; max-width: 800px; margin: 0 auto; padding: 20px; }
  h1 { font-size: 20px; border-bottom: 2px solid #6366f1; padding-bottom: 8px; margin-bottom: 4px; }
  .meta { font-size: 12px; color: #666; margin-bottom: 24px; }
  .page { margin-bottom: 28px; page-break-inside: avoid; }
  .page-url { font-size: 14px; font-weight: 700; color: #6366f1; margin-bottom: 6px; padding: 6px 10px; background: #f0f0ff; border-radius: 6px; }
  .qa { margin: 8px 0; padding: 8px 12px; border-left: 3px solid #e0e0e0; }
  .q { font-weight: 700; color: #1a1a2e; margin-bottom: 2px; }
  .a { color: #333; }
  .keywords { font-size: 11px; color: #888; margin-top: 2px; }
  .schema-block { margin-top: 10px; padding: 10px; background: #f7f7f7; border-radius: 6px; font-family: monospace; font-size: 11px; white-space: pre-wrap; word-break: break-all; page-break-inside: avoid; }
  .schema-label { font-size: 11px; font-weight: 700; color: #8b5cf6; margin-bottom: 4px; }
  .summary { margin: 16px 0 24px; padding: 12px; background: #f0f0ff; border-radius: 8px; font-size: 13px; }
  .footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }
  @media print { .no-print { display: none; } }
</style></head><body>
<button class="no-print" onclick="window.print()" style="position:fixed;top:10px;right:10px;padding:8px 20px;background:#6366f1;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;z-index:99">Друк / PDF</button>
<h1>ТЗ: FAQ + FAQPage Schema.org</h1>
<div class="meta">${domain} &middot; ${new Date().toLocaleDateString("uk-UA")} &middot; ${faq.pages.length} сторінок &middot; ${totalQ} питань</div>
<div class="summary">
  <strong>Завдання:</strong> Додати на кожну сторінку блок FAQ з відповідями-капсулами (40-60 слів) та JSON-LD розмітку FAQPage Schema.org.<br>
  <strong>Мета:</strong> Збільшити видимість в AI-пошуковиках (ChatGPT, Perplexity, Gemini) на 40-60%.
</div>
${faq.pages.map((page, pi) => {
  const path = (() => { try { return new URL(page.url).pathname; } catch { return page.url; } })();
  return `<div class="page">
  <div class="page-url">${pi + 1}. ${path}</div>
  ${(page.items || []).map((item, qi) => `<div class="qa">
    <div class="q">Q${qi + 1}: ${item.question}</div>
    <div class="a">A: ${item.answer}</div>
    ${item.keywords?.length ? `<div class="keywords">Ключі: ${item.keywords.join(", ")}</div>` : ""}
  </div>`).join("")}
  <div class="schema-label">JSON-LD (вставити в &lt;head&gt;):</div>
  <div class="schema-block">${JSON.stringify(page.schema, null, 2).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
</div>`;
}).join("")}
<div class="footer">Згенеровано Spalah SEO Platform &middot; svitlo.agency</div>
</body></html>`);
                  win.document.close();
                }}
                style={{ ...btnSm, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M12 12v6"/><path d="M9 15l3 3 3-3"/></svg>
                PDF ТЗ
              </button>
            )}
            {faq?.pages?.length > 0 && (
              <button
                onClick={async () => {
                  try {
                    const r = await apiFetch(`/api/projects/${activeId}/faq-generator/share-link`, { method: "POST" });
                    const d = await r.json();
                    if (!r.ok || !d.token) { flash("Не вдалося отримати лінк: " + (d.error || ""), "error"); return; }
                    const fullUrl = `${window.location.origin}/api/public/faq-tz/${d.token}`;
                    try { await navigator.clipboard.writeText(fullUrl); } catch {}
                    window.open(fullUrl, "_blank", "noopener");
                    flash("Лінк на ТЗ скопійовано та відкрито у новій вкладці", "success");
                  } catch (e) {
                    flash("Помилка: " + e.message, "error");
                  }
                }}
                style={{ ...btnSm, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(99,102,241,0.08)", color: "#6366f1", border: "1px solid rgba(99,102,241,0.4)" }}
                title="Замість великого PDF — лінк на сторінку з усією FAQ-розміткою. Зручно для великих сайтів."
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                Лінк на ТЗ
              </button>
            )}
            {faq?.pages?.length > 0 && (
              <button
                onClick={async () => {
                  const total = faq.pages.reduce((s, p) => s + (p.items?.length || 0), 0);
                  if (!window.confirm(`Видалити всі ${total} FAQ для ${faq.pages.length} сторінок? Дію не можна скасувати.`)) return;
                  try {
                    const r = await apiFetch(`/api/projects/${activeId}/faq-generator`, { method: "DELETE" });
                    if (!r.ok) { flash("Помилка видалення", "error"); return; }
                    const d = await r.json();
                    setFaq(null);
                    flash(`Видалено ${d.deleted} FAQ`, "success");
                  } catch (e) {
                    flash("Помилка видалення: " + e.message, "error");
                  }
                }}
                style={{ background: "transparent", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 012-2h2a2 2 0 012 2v2"/></svg>
                Видалити все
              </button>
            )}
          </div>

          {faq?.pages?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {faq.pages.map((page, pi) => (
                <div key={pi} style={{ ...card, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{(() => { try { return new URL(page.url).pathname; } catch { return page.url; } })()}</div>
                      <div style={{ fontSize: 11, color: t.textMuted }}>{page.items?.length || 0} питань</div>
                    </div>
                    <button onClick={() => {
                      const schema = JSON.stringify(page.schema, null, 2);
                      copy(schema);
                    }} style={{ ...btnSm, fontSize: 11 }}>Копіювати Schema</button>
                  </div>
                  {(page.items || []).map((item, qi) => (
                    <div key={qi} style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}` }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#6366f1" }}>Q: {item.question}</div>
                      <div style={{ fontSize: 13, lineHeight: 1.6 }}>A: {item.answer}</div>
                      {item.keywords?.length > 0 && (
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                          Ключі: {item.keywords.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {(!faq || !faq.pages?.length) && !generating && (
            <div style={{ ...card, textAlign: "center", padding: "40px 20px", color: t.textMuted }}>
              Натисніть "Згенерувати" — AI створить FAQ з капсульними відповідями для кожної сторінки + Schema.org
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
